/**
 * Single user row in the Admin user list.
 *
 * Encapsulates the per-row controls (select, last-login, role, unlock,
 * active toggle, delete) so AdminPage only has to map() rows. All
 * actions are surfaced as callbacks — the row makes no API calls
 * itself, keeping the page in charge of the user list state.
 */
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Lock, CheckSquare, Square, Unlock as UnlockIcon, Trash2 } from "lucide-react";
import { isUserLocked, relativeTime } from "@/lib/adminFormat";

export default function AdminUserRow({
  user,
  isSelf,
  checked,
  onToggle,
  onRoleChange,
  onActiveChange,
  onUnlock,
  onDelete,
}) {
  const locked = isUserLocked(user);
  const selectable = !isSelf;

  return (
    <div
      className={`p-4 flex flex-wrap items-center gap-3 ${checked ? "bg-red-950/20" : ""}`}
      data-testid={`admin-user-${user.id}`}
    >
      {/* Per-row checkbox. Hidden (placeholder) for the actor's own row
          so they can't select themselves into a bulk delete — the
          backend rejects it anyway, but hiding the affordance saves a
          step. */}
      {selectable ? (
        <button
          type="button"
          onClick={() => onToggle(user.id)}
          aria-label={checked ? `Deselect ${user.email}` : `Select ${user.email}`}
          aria-checked={checked ? "true" : "false"}
          role="checkbox"
          className="text-slate-300 hover:text-slate-100 shrink-0"
          data-testid={`admin-select-${user.id}`}
        >
          {checked ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
        </button>
      ) : (
        <div className="w-4 h-4 shrink-0" aria-hidden="true" />
      )}
      <div className="flex-1 min-w-[180px]">
        <div className="font-medium flex items-center gap-2">
          {user.full_name}
          {locked && (
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-red-300 bg-red-950 border border-red-800 px-2 py-0.5 rounded"
              data-testid={`admin-locked-badge-${user.id}`}
            >
              <Lock className="w-3 h-3" /> LOCKED
            </span>
          )}
        </div>
        <div className="text-xs text-slate-400 font-mono">{user.email}</div>
        {locked && (
          <div className="text-[10px] text-red-300 mt-1">
            Until {new Date(user.locked_until).toLocaleString()} · {user.failed_attempts || 0} failed attempts
          </div>
        )}
      </div>
      {/* Last-login column: relative phrase, full timestamp on hover.
          "Never" rendered dim amber so dormant accounts (especially
          admin-created ones that were never picked up by the employee)
          jump out at a glance. */}
      <div
        className="w-28 text-right shrink-0"
        title={user.last_login_at ? new Date(user.last_login_at).toLocaleString() : "Never signed in"}
        data-testid={`admin-last-login-${user.id}`}
      >
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Last login</div>
        <div className={`text-xs font-mono ${user.last_login_at ? "text-slate-300" : "text-amber-400"}`}>
          {relativeTime(user.last_login_at) || "Never"}
        </div>
      </div>
      <Select value={user.role} onValueChange={(v) => onRoleChange(user.id, v)}>
        <SelectTrigger
          className="bg-slate-950 border-slate-700 w-44"
          data-testid={`admin-role-${user.id}`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="technician">Technician</SelectItem>
          <SelectItem value="foreman">Foreman</SelectItem>
          <SelectItem value="pm">Project Manager</SelectItem>
          <SelectItem value="admin">Administrator</SelectItem>
        </SelectContent>
      </Select>
      {locked && (
        <Button
          size="sm"
          onClick={() => onUnlock(user.id)}
          className="bg-amber-700 hover:bg-amber-600 font-display tracking-wider"
          data-testid={`admin-unlock-${user.id}`}
        >
          <UnlockIcon className="w-4 h-4 mr-1.5" /> UNLOCK
        </Button>
      )}
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400">
          {user.is_active === false ? "Disabled" : "Active"}
        </span>
        <Switch
          checked={user.is_active !== false}
          onCheckedChange={(v) => onActiveChange(user.id, v)}
          data-testid={`admin-active-${user.id}`}
        />
      </div>
      {/* Delete button — hidden on the actor's own row (no self-delete);
          the backend also rejects the lone-admin case so we don't bother
          gating it here. */}
      {selectable && (
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onDelete(user)}
          title={`Delete ${user.email}`}
          aria-label={`Delete ${user.email}`}
          className="text-slate-500 hover:text-red-300 hover:bg-red-950/40"
          data-testid={`admin-delete-${user.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}
